import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DataSetsPanel from 'components/DataSetsPanel/DataSetsPanel';
import DateFieldsWrapper from 'components/DateFieldsWrapper/DateFieldsWrapper';
import NumberField from 'components/NumberField/NumberField';
import Select from 'components/Select/Select';
import TextField from 'components/TextField/TextField';
import { DATA_SETS_ENUM_TYPES } from 'constants/dataSets';
import {
  ASSET_CATEGORY,
  DATE_CLOSED,
  DATE_OPENED,
  EQUITY_SHAREHOLDING,
  FINANCIAL_CONTROL,
  LEGAL_NAME,
  NAME,
  NUMBER_OF_EMPLOYEES,
  OWNED_LEASED,
  OWNERSHIP,
  UNIQUE_IDENTIFIER,
  VALUE_CHAIN_LIST,
  VALUE_CHAIN_SECTION,
} from 'constants/formFields';
import { NUMBER_FORMATS } from 'constants/interfaces';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IFieldError from 'store/types/IFieldError';
import type { IGeneralDataSetsPanel } from '../interfaces/IGeneralDataSetsPanel';

import styles from '../sass/CreateOwnOperationsView.module.scss';

function GeneralDataSetsPanel({
  control,
  enumData,
  recordId,
  isUniqueIdentifierEditable = false,
  placeholder,
  dateOpened,
  onDateOpenedChange,
  vcOptions,
  vcOptionsList,
  handleGetVcOptions,
  listPosition,
  setValue,
}: IGeneralDataSetsPanel) {
  const { t } = useTranslation();

  return (
    <DataSetsPanel title={t('ownOperations.selections.general')} className={styles.fullWidth}>
      <div className={styles.wrapper}>
        <Controller
          name={NAME}
          control={control}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <TextField
              value={value}
              name={name}
              label={t('ownOperations.fields.name')}
              error={error?.message}
              onChange={(event) => onChange(event.target.value)}
              placeholder={t('ownOperations.fields.name')}
              required
            />
          )}
        />
        <Controller
          name={UNIQUE_IDENTIFIER}
          control={control}
          render={({
            field: {
              value,
              name,
              onChange,
            },
            fieldState: { error },
          }) => (
            <TextField
              value={value}
              name={name}
              label={t('ownOperations.fields.uniqueIdentifier')}
              error={error?.message}
              onChange={(event) => onChange(event.target.value)}
              placeholder={t('ownOperations.fields.uniqueIdentifier')}
              disabled={!!recordId && !isUniqueIdentifierEditable}
              required
            />
          )}
        />
      </div>
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={LEGAL_NAME}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <TextField
              name={name}
              value={value}
              label={t('ownOperations.fields.legalEntity')}
              error={error?.message}
              onChange={(event) => onChange(event.target.value)}
              required
              placeholder={t('ownOperations.fields.legalEntity')}
            />
          )}
        />
      </div>
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={VALUE_CHAIN_LIST}
          render={({
            field: { name, value: valueChain, onChange: onChangeValueChain },
            fieldState: { error: errorCurrency },
          }) => (
            <Select
              name={name}
              placeholder={t('common.valueChainPosition')}
              label={t('common.valueChainPosition')}
              options={vcOptionsList}
              value={valueChain?.value ? valueChain : undefined}
              onChange={(value) => {
                const selectedValue = value as IOption;
                onChangeValueChain(selectedValue);
                handleGetVcOptions(selectedValue.value);
                setValue(VALUE_CHAIN_SECTION, undefined as never);
              }}
              error={errorCurrency?.message}
              menuPosition="absolute"
              isDisabled
              required
            />
          )}
        />
        <Controller
          control={control}
          name={VALUE_CHAIN_SECTION}
          render={({
            field: { name, value: valueChainSection, onChange: onChangeValueChainSection },
            fieldState: { error: errorCurrencySection },
          }) => (
            <Select
              name={name}
              placeholder={t('common.valueChainCategory')}
              label={t('common.valueChainCategory')}
              options={vcOptions || listPosition}
              value={valueChainSection?.value ? valueChainSection : null}
              onChange={onChangeValueChainSection}
              error={errorCurrencySection?.message}
              menuPosition="absolute"
              isDisabled={listPosition?.length === 0 && !recordId}
              required
            />
          )}
        />
        <Controller
          name={OWNERSHIP}
          control={control}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <Select
              name={name}
              label={t('ownOperations.fields.ownership')}
              placeholder={recordId ? placeholder : t('ownOperations.fields.ownership')}
              options={enumData?.[DATA_SETS_ENUM_TYPES.ownership]}
              value={value?.value ? value : undefined}
              onChange={onChange}
              error={(error as IFieldError)?.message}
              menuPosition="absolute"
              required
            />
          )}
        />
      </div>
      <div className={styles.wrapper}>
        <Controller
          name={EQUITY_SHAREHOLDING}
          control={control}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <NumberField
              placeholder={t('ownOperations.fields.equityShareholding')}
              value={value}
              name={name}
              error={error?.message}
              label={t('ownOperations.fields.equityShareholding')}
              onChange={(values) => onChange(values.value)}
              type={NUMBER_FORMATS.percent}
              allowNegative={false}
              max={100}
            />
          )}
        />
        <Controller
          control={control}
          name={FINANCIAL_CONTROL}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <NumberField
              value={value}
              placeholder={t('ownOperations.fields.financialControl')}
              type={NUMBER_FORMATS.percent}
              name={name}
              label={t('ownOperations.fields.financialControl')}
              onChange={(values) => onChange(values.value)}
              error={error?.message}
              allowNegative={false}
              max={100}
            />
          )}
        />
      </div>
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={ASSET_CATEGORY}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <Select
              name={name}
              label={t('ownOperations.fields.assetCategory')}
              placeholder={t('ownOperations.fields.assetCategory')}
              options={enumData?.[DATA_SETS_ENUM_TYPES.assetCategory]}
              value={value || undefined}
              onChange={onChange}
              error={(error as IFieldError)?.message}
              menuPosition="absolute"
              required
              isMulti
            />
          )}
        />
        <Controller
          control={control}
          name={OWNED_LEASED}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <Select
              name={name}
              label={t('ownOperations.fields.owned')}
              placeholder={recordId ? placeholder : t('ownOperations.fields.owned')}
              options={enumData?.[DATA_SETS_ENUM_TYPES.ownedLeased]}
              value={value?.value ? value : undefined}
              onChange={onChange}
              error={(error as IFieldError)?.value?.message}
              menuPosition="absolute"
              required
            />
          )}
        />
      </div>
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={NUMBER_OF_EMPLOYEES}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <NumberField
              name={name}
              value={value}
              label={t('ownOperations.fields.employees')}
              error={error?.message}
              onChange={(values) => onChange(values.value)}
              decimalScale={0}
              placeholder={t('ownOperations.fields.employees')}
              allowNegative={false}
            />
          )}
        />
      </div>
      <DateFieldsWrapper
        control={control}
        startFieldName={DATE_OPENED}
        endFieldName={DATE_CLOSED}
        startDate={dateOpened}
        onStartDateChange={onDateOpenedChange}
        startFieldLabel={t('ownOperations.fields.dateOpened')}
        endFieldLabel={t('ownOperations.fields.dateClosed')}
        startRequired
        className={styles.wrapper}
      />
    </DataSetsPanel>
  );
}

export default GeneralDataSetsPanel;
